import { useRef, useState } from "react";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { EASE } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "nome", label: "Nome e cognome", type: "text", placeholder: "Mario Rossi", required: true },
  { name: "telefono", label: "Telefono", type: "tel", placeholder: "es. 333 1234567", required: true },
  { name: "email", label: "Email", type: "email", placeholder: "nome@email.it", required: true },
  { name: "zona", label: "Zona dell'immobile", type: "text", placeholder: "es. Sempione, Milano", required: false },
];

export const Contact = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", zona: "", messaggio: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono || !form.email) {
      toast.error("Compila nome, telefono ed email per continuare.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm({ nome: "", telefono: "", email: "", zona: "", messaggio: "" });
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova o chiamaci al 328 2607116.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" ref={ref} data-testid="contact-section" className="py-24 md:py-36 bg-[#ECEEE7]">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <div className="overline text-[#21463E] mb-6 flex items-center gap-3">
            <span className="h-px w-8 bg-[#21463E] inline-block" /> Contatti
          </div>
          <h2 className="font-serif font-light text-[#2E3131] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Vuoi vendere casa <em className="italic text-[#21463E]">a Milano?</em>
          </h2>
          <p className="mt-6 text-[#5C6360] text-lg leading-relaxed max-w-md">
            Lasciaci i tuoi recapiti: ti richiamiamo per fissare una valutazione gratuita del tuo immobile, senza alcun impegno.
          </p>

          <div className="mt-10 space-y-4 text-[#2E3131]">
            <a href="tel:+393282607116" data-testid="contact-phone" className="block text-2xl font-serif hover:text-[#21463E] transition-colors duration-300">328 2607116</a>
            <a href="mailto:info@albaimmobili.it" data-testid="contact-email" className="block text-lg text-[#5C6360] hover:text-[#2E3131] transition-colors duration-300">info@albaimmobili.it</a>
            <p className="text-[#5C6360]">Via Cenisio 40, Milano</p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: EASE }}
          className="lg:col-span-7 lg:col-start-6 bg-[#F5F6F2] border border-[#DBDBDA] rounded-3xl p-8 md:p-12"
        >
          {done ? (
            <div data-testid="contact-success" className="flex flex-col items-start gap-5 py-10">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#21463E] text-[#F5F6F2]"><Check size={28} /></span>
              <h3 className="font-serif text-3xl text-[#2E3131]">Grazie, richiesta ricevuta.</h3>
              <p className="text-[#5C6360] max-w-md">Ti ricontatteremo al più presto ai recapiti indicati. Per urgenze puoi chiamarci al 328 2607116.</p>
              <button data-testid="contact-reset" onClick={() => setDone(false)} className="mt-2 text-sm underline underline-offset-4 decoration-[#21463E]/60 text-[#2E3131]">Invia un&rsquo;altra richiesta</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "zona" ? "sm:col-span-2" : ""}>
                  <label className="overline text-[#5C6360] mb-2 block">{f.label}{f.required && <span className="text-[#21463E]"> *</span>}</label>
                  <input
                    data-testid={`input-${f.name}`}
                    name={f.name}
                    type={f.type}
                    required={f.required}
                    value={form[f.name]}
                    onChange={update}
                    placeholder={f.placeholder}
                    className="w-full bg-transparent border-b border-[#DBDBDA] py-3 text-[#2E3131] placeholder:text-[#5C6360]/40 focus:border-[#21463E] outline-none transition-colors duration-300"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="overline text-[#5C6360] mb-2 block">Messaggio</label>
                <textarea
                  data-testid="input-messaggio"
                  name="messaggio"
                  rows={3}
                  value={form.messaggio}
                  onChange={update}
                  placeholder="Descrivici brevemente il tuo immobile (mq, piano, stato)..."
                  className="w-full bg-transparent border-b border-[#DBDBDA] py-3 text-[#2E3131] placeholder:text-[#5C6360]/40 focus:border-[#21463E] outline-none transition-colors duration-300 resize-none"
                />
              </div>
              <div className="sm:col-span-2 mt-2">
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#21463E] text-[#F5F6F2] rounded-full px-8 py-4 text-sm hover:bg-[#2E3131] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={18} className="animate-spin" /> Invio in corso...</> : <>Invia la richiesta <span className="inline-block group-hover:translate-x-1.5 transition-transform duration-300">→</span></>}
                </button>
                <p className="mt-4 text-xs text-[#5C6360]/70">Inviando accetti di essere ricontattato da Alba Immobili. I tuoi dati non saranno ceduti a terzi.</p>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
